// Percorso file: src/components/RequireRole.jsx
//
// CANCELLO CONDIVISO — l'unico controllo d'accesso del frontend
// (diario, CONTRATTO punto 5: «UNA sola funzione condivisa di gate nel
// frontend e UNA sola mappa ruolo→rotta»; audit 08 §0.7: prima erano
// 11 gate diversi, nessuno uguale all'altro).
//
// Nessuna home contiene più logica d'accesso propria: le home sono solo
// contenuto, questo componente decide se mostrarle.
//
// Ordine dei controlli (fisso, non riordinabile):
//   1. isReady falso           → nulla a schermo (attesa dell'auth)
//   2. nessun utente           → /login
//   3. status ARCHIVED         → schermata account archiviato
//   4. status PENDING          → /home/pending
//   5. completamento da fare   → /home/incomplete
//   6. ruolo richiesto assente → schermata «Access not allowed»
//   7. altrimenti              → la pagina
//
// ⚠️ I QUATTRO BINARI, e perché l'ordine 4 → 5 non si inverte (RL-6).
// Lo STATUS si guarda PRIMA del marcatore, sempre:
//   PENDING                    → «attendi approvazione» (/home/pending)
//   ACTIVE + qualcosa da fare  → «completa i tuoi dati» (/home/incomplete)
//   ACTIVE, tutto a posto      → la pagina (o la home per ruolo)
//   ARCHIVED                   → schermata account archiviato
// Un PENDING non deve MAI vedere «completa i tuoi dati»: prima
// dell'approvazione non c'è nulla che possa completare, e il suo documento
// nasce comunque con `enrolled` spento. Invertendo i due controlli, ogni
// persona appena iscritta finirebbe sul modulo sbagliato.
//
// I gate del frontend restano cortesia grafica: la protezione vera è
// sul backend (diario, CONTRATTO punto 5).
//
// Props:
//   role   — ruolo richiesto dalla pagina (MAIUSCOLO), oppure un elenco
//            di ruoli accettati: basta possederne UNO. Assente = basta
//            essere un utente attivo (es. /home, /home/context).
//            Un elenco VUOTO non è «assente»: non passa nessuno.
//            ⚠️ L'elenco serve perché `canAccess` è un test di
//            appartenenza secco, senza gerarchia: SUPERADMIN NON è
//            compreso in ADMIN. Una pagina aperta a entrambi deve
//            dirlo, invece di far dedurre al gate una gerarchia che il
//            contratto non prevede (cantiere U1-S).
//   status — status richiesto dalla pagina. Serve alla sola vista
//            d'attesa (/home/pending), che è riservata ai PENDING e non
//            può rimbalzare su se stessa; chi non ha quello status
//            torna all'instradatore.
//   enrolment — vale solo "INCOMPLETE", e serve alla sola vista
//            «completa i tuoi dati» (/home/incomplete), che per la stessa
//            ragione non può rimbalzare su se stessa: è riservata a chi ha
//            il marcatore spento, e chi ce l'ha acceso torna
//            all'instradatore. Non è un ingresso pubblico: ci si arriva
//            solo perché il cancello ce lo manda.
//   router — vale solo per l'instradatore /home (P2, 24/9/2026): lo lascia
//            aperto a chi è SOLO GUEST, che da lì va su /home/guest. Su
//            ogni altra rotta senza `role` il solo GUEST è respinto
//            (passo 6, qui sotto).

import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  canAccess,
  getActiveRoles,
  isEnrolled,
  deveCompletareStagione,
  normalizeRole,
} from "../auth/access";
import { ArchivedView, AccessDeniedView } from "./SystemView";

// Lo status del contratto è MAIUSCOLO (PENDING / ACTIVE / ARCHIVED):
// si normalizza comunque, come si fa con i ruoli.
//
// ⚠️ Si usa la SEDE UNICA (`normalizeRole` di auth/access.js), non una copia
// locale: qui viveva `normalizeStatus`, l'unica copia dell'espressione
// rimasta fuori da access.js (P0 la censì — a diario è detta «terza copia di
// normalizeRole», contando anche la ri-esportazione `normalizza` di
// roleUtils, che copia non è — e P9 la toglie). Il comportamento non cambia
// di nulla, le due erano identiche carattere per carattere; ma una copia che
// diverge dalla fonte è il difetto che l'audit 08 §0.7 contava in undici
// esemplari, uno per home.
//
// ⚠️ È `normalizeRole` e NON `canonicalRole`, e il nome inganna: quella
// funzione fa la sola GRAFIA (spazi e maiuscole) e si applica di proposito
// anche a ciò che ruolo non è — status, team, categorie (access.js).
// Il vocabolario dei ruoli sta invece in `canonicalRole`, e su uno status
// sarebbe un danno.

// Le pagine «di ogni socio» per il solo GUEST (P2, passo 6): lo stesso
// elenco di /more in App.jsx, da cui quelle pagine si raggiungono. Serve
// solo a lui: per chiunque altro una pagina senza `role` resta aperta.
const RUOLI_DEI_SOCI = ["ADMIN", "SUPERADMIN", "PLAYER", "PARENT", "COACH"];

const RequireRole = ({
  role = null,
  status = null,
  enrolment = null,
  router = false,
  children,
}) => {
  const { user, isReady } = useAuth();

  // 1. attesa: finché l'autenticazione non è pronta non si mostra nulla
  if (!isReady) return null;

  // 2. nessun utente
  if (!user) return <Navigate to="/login" replace />;

  const userStatus = normalizeRole(user.status);

  // 3. account archiviato
  if (userStatus === "ARCHIVED") return <ArchivedView />;

  // Pagina riservata a uno status preciso (vista d'attesa).
  if (status) {
    return userStatus === normalizeRole(status) ? (
      children
    ) : (
      <Navigate to="/home" replace />
    );
  }

  // 4. registrazione ricevuta, in attesa di assegnazione
  //
  // ⚠️ STA PRIMA del marcatore, e prima anche della vista «completa i dati»
  // qui sotto: un PENDING che digitasse /home/incomplete viene rimandato di
  // qui alla sua vista d'attesa, che è l'unica che deve vedere.
  if (userStatus === "PENDING") return <Navigate to="/home/pending" replace />;

  // LA FINESTRA DI COMPLETAMENTO — UNA CONDIZIONE SOLA, LETTA DUE VOLTE.
  //
  // I due rami qui sotto sono gemelli e devono restare tali: il primo decide
  // se la finestra si apre, il secondo se ci si viene mandati. Se leggessero
  // due condizioni diverse nascerebbe un rimbalzo — l'instradatore manda alla
  // finestra, la finestra rimanda all'instradatore, e viceversa. Per questo la
  // condizione è UNA VARIABILE, non due espressioni da tenere allineate.
  //
  // Due motivi indipendenti e CONCORDI (RENEW-COMPLETAMENTO): puntano nella
  // stessa direzione, e chi li ha entrambi accesi va alla finestra una volta
  // sola.
  //   • i dati anagrafici non ci sono (`enrolled: false`) — il new user
  //     appena approvato;
  //   • i dati ci sono, ma per l'anno di ENROLLMENT non sono stati confermati
  //     — il rinnovante, che ha `enrolled` acceso dall'anno prima e che senza
  //     questo secondo motivo entrerebbe dritto nell'app.
  //
  // ⚠️ Le due convenzioni sono opposte e vanno lette con attenzione: `enrolled`
  // chiude solo con un `false` esplicito, il completamento manda alla finestra
  // solo con un `true` esplicito. Entrambe, nel dubbio, aprono.
  const daCompletare = !isEnrolled(user) || deveCompletareStagione(user);

  // Pagina riservata a chi deve completare (vista «completa i dati»).
  // Chi non ha nulla da fare qui torna all'instradatore, che lo manderà alla
  // home del suo ruolo.
  if (enrolment === "INCOMPLETE") {
    return daCompletare ? children : <Navigate to="/home" replace />;
  }

  // 5. approvato, ma il completamento non è fatto: nessuna pagina dell'app
  // finché non lo è — i dati mancanti, o la conferma per l'anno di
  // enrollment, o tutti e due.
  //
  // ⚠️ Le due convenzioni vivono dentro i due predicati (access.js), non qui:
  // «campo assente = completo» per `enrolled`, «solo un true esplicito manda
  // al completamento» per la conferma di stagione. Un documento vecchio,
  // privo di entrambi i campi, passa: nessun socio storico resta chiuso
  // fuori.
  if (daCompletare) return <Navigate to="/home/incomplete" replace />;

  // 6. ruolo richiesto non posseduto
  // `null` = prop assente (basta un utente attivo); un elenco VUOTO non è
  // la stessa cosa e non deve aprire nulla: nel dubbio si nega.
  let richiesti = role == null ? null : Array.isArray(role) ? role : [role];

  // ⚠️ IL SOLO GUEST È IN DEFAULT-DENY (P2, 24/9/2026, decisione del
  // committente). «Basta un utente attivo» vale per i soci, non per l'ospite
  // delle clinic: per chi ha SOLO il ruolo GUEST una pagina senza ruolo vale
  // come una pagina dei soci — l'elenco di /more — e il resto lo fa il passo
  // 6 com'è, con la stessa schermata «Access not allowed» che vede già su
  // /more, /users o /clinics. Le pagine che gli restano aperte sono quelle
  // che lo NOMINANO nel proprio elenco (/home/guest, /clinics/calendar/:id),
  // l'instradatore /home (`router`) e le pagine pubbliche, che non passano di
  // qui. «Solo GUEST» si legge come sul server (`soloRuoloOspiteClinic`):
  // ruoli posseduti non vuoti e tutti GUEST — chi porta anche un ruolo del
  // club, o nessun ruolo, resta com'era.
  const posseduti = getActiveRoles(user);
  const soloOspite =
    posseduti.length > 0 && posseduti.every((r) => r === "GUEST");

  if (richiesti == null && !router && soloOspite) {
    richiesti = RUOLI_DEI_SOCI;
  }

  if (richiesti && !richiesti.some((r) => canAccess(user, r))) {
    return (
      <AccessDeniedView
        requiredRole={richiesti[0]}
        heldRoles={getActiveRoles(user)}
      />
    );
  }

  // 7. via libera
  return children;
};

export default RequireRole;
